import { alpha, createTheme, darken, lighten } from '@mui/material';
import '@mui/lab/themeAugmentation';
import { brand, brandFonts, brandGradients } from './brandTokens';
import { checkboxCheckedIcon, checkboxIcon, checkboxIndeterminateIcon } from './checkboxIcons';

const themeColors = {
  primary: brand.blue,
  secondary: brand.ink,
  success: '#00806a',
  warning: '#f59e0b',
  error: '#c2300f',
  info: brand.purple,
  black: brand.ink,
  white: '#ffffff'
};

const tone = (main: string) => ({
  lighter: alpha(main, 0.1),
  light: lighten(main, 0.3),
  main,
  dark: darken(main, 0.25)
});

const colors = {
  primary: tone(themeColors.primary),
  secondary: tone(themeColors.secondary),
  success: tone(themeColors.success),
  warning: tone(themeColors.warning),
  error: tone(themeColors.error),
  info: tone(themeColors.info),
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    black: {
      5: alpha(themeColors.black, 0.04),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black
    }
  }
};

const radius = {
  control: 4,
  small: 8,
  medium: 16,
  card: 20
};

// Secondary actions (List, Export, Columns, Edit...) read as pills, echoing the ink tabs.
const actionPill = {
  borderRadius: 9999,
  border: `1px solid ${brand.border}`,
  backgroundColor: themeColors.white,
  color: brand.ink,
  padding: '6px 14px',
  minHeight: 36,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.4,
  '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: brand.ink },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
  '&:hover': { backgroundColor: themeColors.white, borderColor: brand.ink }
};

const actionPillPrimary = {
  ...actionPill,
  backgroundColor: brand.ink,
  borderColor: brand.ink,
  color: themeColors.white,
  '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: themeColors.white },
  '&:hover': { backgroundColor: '#000000', borderColor: '#000000' }
};

const actionPillDanger = {
  ...actionPill,
  color: '#c2300f',
  borderColor: alpha('#c2300f', 0.35),
  '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#c2300f' },
  '&:hover': { backgroundColor: alpha('#c2300f', 0.06), borderColor: '#c2300f' }
};

const floatingShadow = '0 4px 100px 0 rgba(117, 128, 139, 0.2)';
const popoverShadow =
  '0 12px 32px -8px rgba(23, 23, 23, 0.18), 0 2px 6px rgba(23, 23, 23, 0.06)';

// Global rules for react-admin surfaces. The app does not mount CssBaseline, so these
// are rendered with GlobalStyles by the admin layout.
export const brandGlobalStyles = {
  '::selection': { background: alpha(brand.purple, 0.18) },
  // react-admin links wrap their text in a Typography that would keep the primary colour.
  '.MuiLink-root.RaLink-link .MuiTypography-root': { color: 'inherit' },
  // Bulk selection bar: a neutral band that opens above the table header instead of
  // overlaying the filter bar (whose height varies with the filters).
  // Three classes: outranks react-admin's own two-class rule, injected after this one.
  '.MuiToolbar-root.RaBulkActionsToolbar-toolbar.RaBulkActionsToolbar-toolbar': {
    position: 'static',
    transform: 'none',
    height: 56,
    minHeight: 56,
    padding: '0 20px !important',
    borderRadius: 0,
    borderTop: `1px solid ${brand.borderSoft}`,
    borderBottom: `1px solid ${brand.borderSoft}`,
    backgroundColor: brand.surfaceMuted,
    color: brand.ink,
    '& .RaBulkActionsToolbar-title': { alignItems: 'center', gap: 4, fontWeight: 600 },
    '& .RaBulkActionsToolbar-title .MuiTypography-root': { fontSize: 14, fontWeight: 600 },
    '& .RaBulkActionsToolbar-icon': { marginLeft: -8, marginRight: 4 },
    '& .RaBulkActionsToolbar-topToolbar': { paddingBottom: 0, backgroundColor: 'transparent' }
  },
  '.MuiToolbar-root.RaBulkActionsToolbar-toolbar.RaBulkActionsToolbar-collapsed': {
    height: 0,
    minHeight: 0,
    borderTop: 0,
    borderBottom: 0
  },
  // Confirmation dialogs: the destructive choice is a red button, cancelling is quiet.
  '.MuiDialogActions-root': { padding: '0 24px 20px', gap: 8 },
  '.MuiDialogActions-root .MuiButton-root': { borderRadius: 9999, padding: '8px 18px' },
  '.MuiDialogActions-root .RaConfirm-confirmWarning': {
    backgroundColor: '#c2300f',
    color: `${themeColors.white} !important`,
    '& .MuiButton-startIcon': { color: themeColors.white },
    '&:hover': { backgroundColor: '#9a260c' }
  },
  '.MuiDialogActions-root .RaConfirm-confirmPrimary': {
    backgroundColor: brand.ink,
    color: `${themeColors.white} !important`,
    '& .MuiButton-startIcon': { color: themeColors.white },
    '&:hover': { backgroundColor: '#000000' }
  },
  // react-admin wraps Create/Edit/Show in its own card; ours already frame the page.
  '.RaCreate-card, .RaEdit-card, .RaShow-card': {
    background: 'transparent',
    border: '0 !important',
    borderRadius: 0,
    overflow: 'visible'
  }
};

export const brandTheme = createTheme({
  colors: {
    gradients: {
      blue1: brandGradients.button,
      blue2: brandGradients.button,
      blue3: brandGradients.button,
      blue4: brandGradients.button,
      blue5: brandGradients.button,
      orange1: brandGradients.orange,
      orange2: brandGradients.orange,
      orange3: brandGradients.orange,
      purple1: brandGradients.purple,
      purple3: brandGradients.purple,
      pink1: brandGradients.purple,
      pink2: brandGradients.purple,
      green1: brandGradients.green,
      green2: brandGradients.green,
      black1: `linear-gradient(100deg, ${lighten(brand.ink, 0.15)} 0%, ${brand.ink} 100%)`,
      black2: `linear-gradient(100deg, ${lighten(brand.ink, 0.15)} 0%, ${brand.ink} 100%)`
    },
    shadows: {
      success: 'none',
      error: 'none',
      primary: 'none',
      info: 'none',
      warning: 'none'
    },
    alpha: colors.alpha,
    secondary: colors.secondary,
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info
  },
  general: {
    reactFrameworkColor: brand.blue,
    borderRadiusSm: `${radius.control}px`,
    borderRadius: `${radius.small}px`,
    borderRadiusLg: `${radius.medium}px`,
    borderRadiusXl: `${radius.card}px`
  },
  sidebar: {
    background: brand.ink,
    textColor: alpha(themeColors.white, 0.7),
    dividerBg: alpha(themeColors.white, 0.08),
    menuItemColor: alpha(themeColors.white, 0.7),
    menuItemColorActive: themeColors.white,
    menuItemBg: 'transparent',
    menuItemBgActive: alpha(themeColors.white, 0.08),
    menuItemIconColor: alpha(themeColors.white, 0.45),
    menuItemIconColorActive: themeColors.white,
    menuItemHeadingColor: alpha(themeColors.white, 0.45),
    boxShadow: 'none',
    width: '290px'
  },
  header: {
    height: '72px',
    background: brand.page,
    boxShadow: 'none',
    textColor: brand.ink
  },
  spacing: 9,
  palette: {
    mode: 'light',
    common: { black: brand.ink, white: themeColors.white },
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
      contrastText: themeColors.white
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: '#000000',
      contrastText: themeColors.white
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: '#9a260c',
      contrastText: themeColors.white
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: '#005f4f',
      contrastText: themeColors.white
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: themeColors.white
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: '#a15c00',
      contrastText: themeColors.white
    },
    text: {
      primary: brand.ink,
      secondary: brand.textSecondary,
      disabled: brand.muted
    },
    divider: brand.borderSoft,
    background: {
      paper: brand.surface,
      default: brand.page
    },
    action: {
      active: brand.ink,
      hover: alpha(brand.ink, 0.04),
      hoverOpacity: 0.04,
      selected: alpha(brand.blue, 0.08),
      selectedOpacity: 0.08,
      disabled: brand.muted,
      disabledBackground: alpha(brand.ink, 0.06),
      disabledOpacity: 0.38,
      focus: alpha(brand.blue, 0.12),
      focusOpacity: 0.12,
      activatedOpacity: 0.12
    },
    tonalOffset: 0.5
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1840 }
  },
  shape: { borderRadius: radius.small },
  typography: {
    fontFamily: brandFonts.body,
    fontWeightBold: 600,
    h1: {
      fontFamily: brandFonts.heading,
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.15,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontFamily: brandFonts.heading,
      fontWeight: 600,
      fontSize: 26,
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontFamily: brandFonts.heading,
      fontWeight: 600,
      fontSize: 22,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: brand.ink
    },
    h4: {
      fontFamily: brandFonts.heading,
      fontWeight: 600,
      fontSize: 16,
      lineHeight: 1.35,
      letterSpacing: '-0.005em'
    },
    h5: { fontWeight: 600, fontSize: 14 },
    h6: {
      fontFamily: brandFonts.heading,
      fontWeight: 600,
      fontSize: 16,
      lineHeight: 1.35,
      letterSpacing: '-0.005em'
    },
    body1: { fontSize: 14, lineHeight: 1.5 },
    body2: { fontSize: 14, lineHeight: 1.5 },
    button: { fontWeight: 500, fontSize: 14, letterSpacing: 0 },
    caption: {
      fontSize: 12,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: brand.textSecondary
    },
    subtitle1: { fontSize: 14, color: brand.textSecondary },
    subtitle2: { fontWeight: 400, fontSize: 15, color: brand.textSecondary },
    overline: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': { width: '100%', height: '100%' },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased'
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          flex: 1,
          backgroundColor: brand.page,
          color: brand.ink
        },
        '#root': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        },
        '.child-popover .MuiPaper-root .MuiList-root': { flexDirection: 'column' },
        ':root': { '--swiper-theme-color': brand.blue },
        code: {
          fontFamily: brandFonts.mono,
          background: brand.tintViolet,
          color: darken(brand.purple, 0.2),
          borderRadius: radius.control,
          padding: '2px 4px'
        },
        '@keyframes pulse': {
          '0%': { transform: 'scale(.75)' },
          '20%': { transform: 'scale(1.1)' },
          '40%': { transform: 'scale(.75)' },
          '60%': { transform: 'scale(1.05)' },
          '80%': { transform: 'scale(.75)' },
          '100%': { transform: 'scale(.75)' }
        },
        '@keyframes ripple': {
          '0%': { transform: 'scale(.8)', opacity: 1 },
          '100%': { transform: 'scale(2.8)', opacity: 0 }
        },
        '@keyframes float': {
          '0%': { transform: 'translate(0%, 0%)' },
          '100%': { transform: 'translate(3%, 3%)' }
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(brand.ink, 0.35),
          backdropFilter: 'blur(4px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'none'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { padding: 0, backgroundImage: 'none' },
        rounded: { borderRadius: radius.medium },
        elevation0: { boxShadow: 'none' },
        elevation: { boxShadow: 'none' },
        elevation2: { boxShadow: 'none' },
        elevation24: { boxShadow: floatingShadow },
        outlined: { boxShadow: 'none', borderColor: brand.borderSoft }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          boxShadow: 'none',
          overflow: 'hidden',
          // A card inside a card (a section of a detail page) needs an edge to read as a group.
          '& .MuiCard-root:not(.RaCreate-card):not(.RaEdit-card):not(.RaShow-card)': {
            borderRadius: radius.medium,
            border: `1px solid ${brand.borderSoft}`
          }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '18px 20px' },
        action: { marginTop: -5, marginBottom: -5 },
        title: {
          fontFamily: brandFonts.heading,
          fontWeight: 600,
          fontSize: 16,
          letterSpacing: '-0.005em'
        },
        subheader: { color: brand.textSecondary }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 }
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radius.medium,
          boxShadow: popoverShadow,
          border: `1px solid ${brand.borderSoft}`
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.card, boxShadow: floatingShadow }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: brandFonts.heading,
          fontWeight: 600,
          fontSize: 20,
          letterSpacing: '-0.01em'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRadius: 0 } }
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          // Ripples are disabled, so keyboard focus needs its own visible cue.
          '&.Mui-focusVisible': {
            outline: `2px solid ${brand.blue}`,
            outlineOffset: 2
          }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          fontWeight: 500,
          textTransform: 'none',
          paddingLeft: 16,
          paddingRight: 16,
          transition: 'background-color .2s, color .2s, border-color .2s',
          '.MuiSvgIcon-root': { transition: 'all .2s' },
          '&.Mui-focusVisible': {
            outline: `2px solid ${brand.blue}`,
            outlineOffset: 2
          }
        },
        endIcon: { marginRight: -4 },
        // Signature CTA: brand gradient, solid ink on hover.
        containedPrimary: {
          background: brandGradients.button,
          backgroundColor: brand.purple,
          color: themeColors.white,
          '&:hover': { background: brand.ink, backgroundColor: brand.ink },
          '&.Mui-disabled': {
            background: alpha(brand.ink, 0.08),
            color: brand.muted
          }
        },
        containedSecondary: {
          backgroundColor: brand.ink,
          color: themeColors.white,
          '&:hover': { backgroundColor: '#000000' }
        },
        containedInherit: {
          backgroundColor: themeColors.white,
          color: brand.ink,
          '&:hover': {
            background: brandGradients.button,
            color: themeColors.white
          }
        },
        // Secondary actions anywhere read as pills, like the ones in action bars.
        outlined: {
          borderRadius: 9999,
          borderColor: brand.border,
          backgroundColor: themeColors.white,
          '&:hover': { borderColor: brand.ink, backgroundColor: themeColors.white }
        },
        outlinedPrimary: {
          color: brand.ink,
          borderColor: brand.ink,
          '&:hover': {
            backgroundColor: brand.ink,
            borderColor: brand.ink,
            color: themeColors.white
          }
        },
        outlinedSecondary: {
          backgroundColor: themeColors.white,
          borderColor: brand.border,
          color: brand.ink,
          '&:hover, &.MuiSelected': {
            backgroundColor: themeColors.white,
            borderColor: brand.ink,
            color: brand.ink
          }
        },
        textPrimary: {
          color: brand.ink,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: brand.textSecondary },
          '&:hover': { backgroundColor: alpha(brand.ink, 0.06) }
        },
        sizeSmall: { padding: '5px 12px', lineHeight: 1.5, fontSize: 13 },
        sizeMedium: { padding: '8px 18px' },
        sizeLarge: { padding: '12px 22px', fontSize: 16, minHeight: 48 },
        textSizeSmall: { padding: '5px 10px' },
        textSizeMedium: { padding: '8px 14px' },
        textSizeLarge: { padding: '12px 16px' }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.small,
          padding: 8,
          '&:hover': { backgroundColor: alpha(brand.ink, 0.06) },
          '& .MuiTouchRipple-root': { borderRadius: radius.small }
        },
        colorPrimary: {
          color: brand.ink,
          '&:hover': { backgroundColor: alpha(brand.ink, 0.06) }
        },
        sizeSmall: { padding: 4 }
      }
    },
    MuiToggleButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: brand.ink,
          background: themeColors.white,
          borderColor: brand.border,
          textTransform: 'none',
          transition: 'all .2s',
          '&:hover': { background: alpha(brand.ink, 0.04) },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: themeColors.white,
            background: brand.ink
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          background: brandGradients.button,
          boxShadow: 'none',
          '&:hover': { background: brand.ink }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: brand.textSecondary,
          '&.Mui-focused': { color: brand.blue },
          '&.Mui-error, &.Mui-error.Mui-focused': { color: themeColors.error }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: themeColors.white,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: brand.border },
          '& .MuiInputAdornment-positionEnd.MuiInputAdornment-outlined': {
            paddingRight: 6
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: brand.ink },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.blue,
            borderWidth: 1.5
          },
          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.blue
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-error:hover .MuiOutlinedInput-notchedOutline, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
            { borderColor: brand.red },
          '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.borderSoft
          }
        }
      }
    },
    // react-admin inputs default to the filled variant: brief input style (grey fill, grey border).
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: brand.surfaceMuted,
          border: `1px solid ${brand.border}`,
          transition: 'border-color .2s, background-color .2s',
          '&:hover': {
            backgroundColor: brand.surfaceMuted,
            borderColor: brand.ink
          },
          '&.Mui-focused': {
            backgroundColor: themeColors.white,
            borderColor: brand.blue,
            boxShadow: `0 0 0 3px ${alpha(brand.blue, 0.12)}`
          },
          '&.Mui-error, &.Mui-error:hover, &.Mui-error.Mui-focused': {
            borderColor: brand.red
          },
          '&.Mui-disabled': {
            backgroundColor: alpha(brand.ink, 0.04),
            borderColor: brand.borderSoft
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { textTransform: 'none', marginLeft: 4, marginRight: 4 }
      }
    },
    MuiSelect: {
      styleOverrides: {
        iconOutlined: { color: brand.textSecondary },
        icon: { top: 'calc(50% - 12px)' }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        icon: checkboxIcon,
        checkedIcon: checkboxCheckedIcon,
        indeterminateIcon: checkboxIndeterminateIcon
      },
      styleOverrides: {
        root: {
          padding: 8,
          borderRadius: radius.small,
          '&:hover': { backgroundColor: 'transparent' },
          '& .brand-checkbox': {
            width: 18,
            height: 18,
            boxSizing: 'border-box',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            border: '1.5px solid #7d8793',
            backgroundColor: themeColors.white,
            color: themeColors.white,
            transition: 'background-color .15s, border-color .15s'
          },
          '&:hover .brand-checkbox': { borderColor: brand.ink },
          '& .brand-checkbox--on': { backgroundColor: brand.ink, borderColor: brand.ink },
          '&.Mui-disabled .brand-checkbox': { opacity: 0.4 }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          color: brand.textSecondary,
          '&.Mui-checked': { color: brand.ink },
          '&.Mui-disabled': { color: brand.muted }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': { color: themeColors.white },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: brand.blue,
            opacity: 1
          },
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.38 }
        },
        thumb: { boxShadow: `0 0 0 1px ${brand.border}` },
        track: { backgroundColor: brand.border, opacity: 1 }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 9999, fontWeight: 500 },
        colorSecondary: {
          background: alpha(brand.ink, 0.06),
          color: brand.ink,
          '&:hover': { background: alpha(brand.ink, 0.1) }
        },
        deleteIcon: {
          color: alpha(brand.ink, 0.4),
          '&:hover': { color: brand.ink }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: 14, fontWeight: 600 },
        colorDefault: { background: alpha(brand.ink, 0.1), color: brand.ink }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: { alignItems: 'center' },
        avatar: {
          background: alpha(brand.ink, 0.08),
          fontSize: 13,
          color: brand.textSecondary,
          fontWeight: 600,
          '&:first-of-type': { border: 0, background: 'transparent' }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&.Mui-expanded': { margin: 0 },
          '&::before': { display: 'none' }
        }
      }
    },
    MuiListItemAvatar: {
      styleOverrides: { alignItemsFlexStart: { marginTop: 0 } }
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 600,
          lineHeight: '40px',
          fontSize: 12,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          background: brand.surfaceMuted,
          color: brand.textSecondary
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiListItem-button': {
            transition: 'all .2s',
            '& > .MuiSvgIcon-root': { minWidth: 34 }
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(brand.blue, 0.08)
          }
        },
        padding: {
          padding: '8px',
          '& .MuiListItem-button': { borderRadius: radius.small, margin: '1px 0' }
        }
      }
    },
    MuiListItemText: { styleOverrides: { root: { margin: 0 } } },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: brand.ink,
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: brand.ink,
              background: alpha(brand.ink, 0.04)
            }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: { padding: 6 },
        list: {
          padding: 0,
          '& .MuiMenuItem-root.MuiButtonBase-root': {
            fontSize: 14,
            borderRadius: radius.small,
            marginTop: 1,
            marginBottom: 1,
            color: brand.ink,
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: brand.ink,
              background: alpha(brand.ink, 0.05)
            }
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: 'transparent',
          borderRadius: radius.small,
          transition: 'background-color .15s',
          '&:hover, &:active, &.active, &.Mui-selected': {
            color: brand.ink,
            background: alpha(brand.ink, 0.05)
          },
          '&.Mui-selected:hover': { background: alpha(brand.ink, 0.08) }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: 1 },
        paper: { borderRadius: radius.medium, boxShadow: popoverShadow },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment':
            { right: 14 }
        },
        clearIndicator: {
          color: brand.textSecondary,
          marginRight: 4,
          '&:hover': { background: alpha(brand.ink, 0.06), color: brand.ink }
        },
        popupIndicator: {
          color: brand.textSecondary,
          '&:hover': { background: alpha(brand.ink, 0.06), color: brand.ink }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: { height: 38, minHeight: 38, overflow: 'visible' },
        indicator: {
          height: 38,
          minHeight: 38,
          borderRadius: 9999,
          backgroundColor: brand.ink
        },
        scrollableX: { overflow: 'visible !important' }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          height: 38,
          minHeight: 38,
          borderRadius: 9999,
          transition: 'color .2s',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 14,
          color: brand.textSecondary,
          '&.MuiButtonBase-root': {
            minWidth: 'auto',
            paddingLeft: 18,
            paddingRight: 18,
            marginRight: 4
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: themeColors.white,
            zIndex: 5
          },
          '&:hover': { color: brand.ink }
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: { fontSize: 13, fontWeight: 600, transition: 'all .2s' },
        textPrimary: {
          '&.Mui-selected': { background: brand.ink, color: themeColors.white },
          '&.MuiButtonBase-root:hover': { background: alpha(brand.ink, 0.06) },
          '&.Mui-selected.MuiButtonBase-root:hover': { background: brand.ink }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: { '& .MuiIconButton-root': { padding: 8 } },
        select: { '&:focus': { backgroundColor: 'transparent' } },
        selectLabel: { color: brand.textSecondary },
        displayedRows: { color: brand.textSecondary }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: '0 !important', padding: '0 !important' }
      }
    },
    // react-admin's TopToolbar: list, detail and bulk actions.
    RaTopToolbar: {
      styleOverrides: {
        root: {
          gap: 8,
          alignItems: 'center',
          '& .MuiButton-text, & .MuiButton-outlined': actionPill,
          '& .MuiButton-root.RaCreateButton-root': actionPillPrimary,
          '& .MuiButton-root.ra-delete-button, & .MuiButton-root[aria-label="Delete"]': actionPillDanger
        }
      }
    },
    RaToolbar: {
      styleOverrides: {
        root: {
          '&.RaToolbar-desktopToolbar': {
            justifyContent: 'flex-end',
            gap: 12,
            backgroundColor: 'transparent',
            borderTop: `1px solid ${brand.borderSoft}`,
            marginTop: 24,
            padding: '20px 0 0 !important'
          },
          '&.RaToolbar-mobileToolbar': {
            backgroundColor: themeColors.white,
            borderTop: `1px solid ${brand.borderSoft}`,
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) !important'
          },
          // Primary action on the right, destructive action on the left.
          '& .RaToolbar-defaultToolbar': {
            flexDirection: 'row-reverse',
            gap: 12
          },
          '& .RaToolbar-defaultToolbar > .MuiButton-root:first-of-type': { minWidth: 140 },
          '& .ra-delete-button': actionPillDanger
        }
      }
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          // Wide tables scroll inside their card instead of being clipped by it.
          // A soft shadow appears on the edge that still has columns to scroll to.
          '& .RaDatagrid-tableWrapper': {
            overflowX: 'auto',
            background: [
              `linear-gradient(to right, ${brand.surface} 30%, rgba(255,255,255,0)) left center / 40px 100% no-repeat local`,
              `linear-gradient(to left, ${brand.surface} 30%, rgba(255,255,255,0)) right center / 40px 100% no-repeat local`,
              'radial-gradient(farthest-side at 0 50%, rgba(23,23,23,.14), transparent) left center / 14px 100% no-repeat scroll',
              'radial-gradient(farthest-side at 100% 50%, rgba(23,23,23,.14), transparent) right center / 14px 100% no-repeat scroll'
            ].join(', ')
          },
          // One row rhythm for every list, whether or not the page adds its own styles.
          '& .RaDatagrid-headerCell': {
            backgroundColor: brand.surfaceMuted,
            borderBottom: `1px solid ${brand.borderSoft}`,
            height: 44,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 14,
            paddingRight: 14,
            whiteSpace: 'nowrap'
          },
          '& .RaDatagrid-rowCell': {
            height: 52,
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 14,
            paddingRight: 14,
            fontSize: 14
          },
          '& .RaDatagrid-headerCell:first-of-type': { paddingLeft: 20 },
          '& .RaDatagrid-rowCell:first-of-type': { paddingLeft: 20 },
          // Selection column: header and row checkboxes on the same axis.
          '& .RaDatagrid-headerCell.MuiTableCell-paddingCheckbox, & .RaDatagrid-row .MuiTableCell-paddingCheckbox':
            { width: 52, padding: '0 0 0 12px' },
          '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': {
            paddingRight: 20
          },
          '& .RaDatagrid-rowCell .MuiButton-root': {
            borderRadius: 9999,
            color: brand.ink,
            padding: '4px 10px',
            fontSize: 13,
            '& .MuiButton-startIcon': { color: brand.textSecondary },
            '&:hover': { backgroundColor: alpha(brand.ink, 0.06) }
          },
          // Long text and links are cut with an ellipsis so the table fits its card.
          '& .RaDatagrid-rowCell > .MuiTypography-root, & .RaDatagrid-rowCell .RaReferenceField-link': {
            display: 'inline-block',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom'
          },
          '& .RaDatagrid-rowCell a.MuiLink-root, & .RaDatagrid-rowCell a.MuiLink-root .MuiTypography-root':
            { color: brand.ink },
          '& .RaDatagrid-rowCell a.MuiLink-root:hover, & .RaDatagrid-rowCell a.MuiLink-root:hover .MuiTypography-root':
            { color: brand.purple },
          '& .RaDatagrid-rowCell .MuiButton-root.ra-delete-button, & .RaDatagrid-rowCell .ra-delete-button .MuiButton-startIcon':
            { color: '#c2300f' }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        head: { background: 'transparent' },
        root: {
          transition: 'background-color .15s',
          '&.MuiTableRow-hover:hover': { backgroundColor: brand.surfaceMuted },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(brand.blue, 0.06)
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: brand.borderSoft, fontSize: 14 },
        head: {
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: brand.textSecondary,
          backgroundColor: themeColors.white,
          borderBottomColor: brand.borderSoft,
          '& .MuiTableSortLabel-root.Mui-active': { color: brand.ink }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: { background: brand.borderSoft, border: 0, height: 1 },
        vertical: {
          height: 'auto',
          width: 1,
          '&.MuiDivider-flexItem.MuiDivider-fullWidth': { height: 'auto' },
          '&.MuiDivider-absolute.MuiDivider-fullWidth': { height: '100%' }
        },
        withChildren: { '&:before, &:after': { border: 0 } },
        wrapper: {
          background: themeColors.white,
          fontWeight: 600,
          height: 24,
          lineHeight: '24px',
          marginTop: -12,
          color: 'inherit',
          textTransform: 'uppercase'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.small },
        message: { lineHeight: 1.5, fontSize: 14 },
        standardInfo: { color: darken(brand.purple, 0.3), background: brand.tintViolet },
        action: { color: brand.textSecondary }
      }
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: { borderRadius: radius.small, backgroundColor: brand.ink }
      }
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: {
          color: brand.ink,
          fontWeight: 500,
          textDecorationColor: alpha(brand.ink, 0.3),
          transition: 'color .15s',
          '&:hover': { color: brand.purple, textDecorationColor: brand.purple }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 6, height: 6, backgroundColor: alpha(brand.ink, 0.08) },
        barColorPrimary: { background: brandGradients.button }
      }
    },
    MuiCircularProgress: {
      styleOverrides: { colorPrimary: { color: brand.purple } }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': { transform: 'none' },
          '& .MuiSlider-valueLabel': {
            borderRadius: radius.control,
            background: brand.ink,
            color: themeColors.white
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.ink,
          borderRadius: radius.control,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 500
        },
        arrow: { color: brand.ink }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          background: brand.surfaceMuted,
          borderRadius: radius.medium
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: brand.border,
          '&.Mui-active': { color: brand.ink },
          '&.Mui-completed': { color: brand.ink }
        }
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: 14,
          fontWeight: 500,
          color: brand.textSecondary,
          '&.Mui-active, &.Mui-completed': { color: brand.ink, fontWeight: 600 }
        }
      }
    },
    MuiStepConnector: {
      styleOverrides: { line: { borderColor: brand.border } }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          margin: 0,
          zIndex: 5,
          position: 'absolute',
          top: '50%',
          marginTop: -6,
          left: -6
        },
        outlined: {
          backgroundColor: themeColors.white,
          boxShadow: '0 0 0 6px ' + themeColors.white
        },
        outlinedPrimary: {
          backgroundColor: themeColors.white,
          boxShadow: '0 0 0 6px ' + themeColors.white
        }
      }
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          position: 'absolute',
          height: '100%',
          top: 0,
          borderRadius: 50,
          backgroundColor: brand.borderSoft
        }
      }
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: { minHeight: 0, padding: '8px 0', '&:before': { display: 'none' } },
        missingOppositeContent: { '&:before': { display: 'none' } }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div'
        }
      },
      styleOverrides: {
        gutterBottom: { marginBottom: 4 },
        paragraph: { fontSize: 16, lineHeight: 1.6 }
      }
    }
  },
  // Only floating surfaces (menus, pickers, dialogs) carry a shadow; cards stay flat.
  shadows: [
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
    popoverShadow, popoverShadow, popoverShadow, popoverShadow, popoverShadow,
    popoverShadow, popoverShadow, popoverShadow, popoverShadow, popoverShadow,
    popoverShadow, popoverShadow, popoverShadow, popoverShadow, popoverShadow,
    popoverShadow, floatingShadow
  ]
});
